/**
 * Utility functions for duplicate detection and text similarity analysis
 */

/**
 * Normalize text for comparison by removing punctuation, converting to lowercase,
 * and trimming whitespace
 * @param {string} text - The text to normalize
 * @returns {string} - Normalized text
 */
export const normalizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Calculate Jaccard similarity between two texts based on word sets
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @returns {number} - Similarity score between 0 and 1
 */
export const calculateJaccardSimilarity = (text1, text2) => {
  const normalized1 = normalizeText(text1);
  const normalized2 = normalizeText(text2);
  
  if (!normalized1 || !normalized2) return 0;
  if (normalized1 === normalized2) return 1;
  
  const words1 = new Set(normalized1.split(' '));
  const words2 = new Set(normalized2.split(' '));
  
  const intersection = new Set([...words1].filter(word => words2.has(word)));
  const union = new Set([...words1, ...words2]);
  
  return intersection.size / union.size;
};

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Edit distance
 */
export const calculateLevenshteinDistance = (str1, str2) => {
  const matrix = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
};

/**
 * Calculate similarity based on Levenshtein distance
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @returns {number} - Similarity score between 0 and 1
 */
export const calculateLevenshteinSimilarity = (text1, text2) => {
  const normalized1 = normalizeText(text1);
  const normalized2 = normalizeText(text2);
  
  if (!normalized1 || !normalized2) return 0;
  if (normalized1 === normalized2) return 1;
  
  const distance = calculateLevenshteinDistance(normalized1, normalized2);
  const maxLength = Math.max(normalized1.length, normalized2.length);
  
  return 1 - (distance / maxLength);
};

/**
 * Calculate combined similarity score using multiple algorithms
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @returns {number} - Combined similarity score between 0 and 1
 */
export const calculateCombinedSimilarity = (text1, text2) => {
  const jaccardScore = calculateJaccardSimilarity(text1, text2);
  const levenshteinScore = calculateLevenshteinSimilarity(text1, text2);
  
  // Weighted average: Jaccard is better for word-level similarity,
  // Levenshtein is better for character-level similarity
  return (jaccardScore * 0.7) + (levenshteinScore * 0.3);
};

/**
 * Detect duplicate questions based on text similarity
 * @param {Array} questions - Array of question objects
 * @param {number} threshold - Similarity threshold (0-1)
 * @param {string} algorithm - Similarity algorithm ('jaccard', 'levenshtein', 'combined')
 * @returns {Array} - Array of duplicate groups
 */
export const detectDuplicates = (questions, threshold = 0.8, algorithm = 'combined') => {
  if (!questions || questions.length < 2) return [];
  
  const duplicateGroups = [];
  const processed = new Set();
  
  const getSimilarity = (text1, text2) => {
    switch (algorithm) {
      case 'jaccard':
        return calculateJaccardSimilarity(text1, text2);
      case 'levenshtein':
        return calculateLevenshteinSimilarity(text1, text2);
      case 'combined':
      default:
        return calculateCombinedSimilarity(text1, text2);
    }
  };
  
  questions.forEach((question, index) => {
    if (processed.has(index)) return;
    
    const duplicates = [{ question, index, similarity: 1.0 }];
    
    for (let i = index + 1; i < questions.length; i++) {
      if (processed.has(i)) continue;
      
      const similarity = getSimilarity(
        question.questionText,
        questions[i].questionText
      );
      
      if (similarity >= threshold) {
        duplicates.push({ 
          question: questions[i], 
          index: i, 
          similarity: similarity 
        });
        processed.add(i);
      }
    }
    
    if (duplicates.length > 1) {
      // Sort by similarity (highest first)
      duplicates.sort((a, b) => b.similarity - a.similarity);
      duplicateGroups.push(duplicates);
      duplicates.forEach(({ index }) => processed.add(index));
    }
  });
  
  return duplicateGroups;
};

/**
 * Get duplicate statistics
 * @param {Array} duplicateGroups - Array of duplicate groups
 * @returns {Object} - Statistics object
 */
export const getDuplicateStats = (duplicateGroups) => {
  const totalGroups = duplicateGroups.length;
  const totalQuestions = duplicateGroups.reduce((sum, group) => sum + group.length, 0);
  const totalDuplicates = duplicateGroups.reduce((sum, group) => sum + group.length - 1, 0);
  
  return {
    totalGroups,
    totalQuestions,
    totalDuplicates,
    uniqueQuestions: totalQuestions - totalDuplicates
  };
};

/**
 * Remove duplicates from questions array, keeping the first occurrence of each group
 * @param {Array} questions - Array of question objects
 * @param {Array} indicesToRemove - Array of indices to remove
 * @returns {Array} - Filtered questions array
 */
export const removeDuplicatesByIndices = (questions, indicesToRemove) => {
  const sortedIndices = [...indicesToRemove].sort((a, b) => b - a);
  const filteredQuestions = [...questions];
  
  sortedIndices.forEach(index => {
    if (index >= 0 && index < filteredQuestions.length) {
      filteredQuestions.splice(index, 1);
    }
  });
  
  return filteredQuestions;
};

/**
 * Auto-remove duplicates keeping the first occurrence of each group
 * @param {Array} questions - Array of question objects
 * @param {number} threshold - Similarity threshold
 * @param {string} algorithm - Similarity algorithm
 * @returns {Object} - Result object with filtered questions and stats
 */
export const autoRemoveDuplicates = (questions, threshold = 0.8, algorithm = 'combined') => {
  const duplicateGroups = detectDuplicates(questions, threshold, algorithm);
  
  if (duplicateGroups.length === 0) {
    return {
      questions: questions,
      removedCount: 0,
      duplicateGroups: [],
      stats: getDuplicateStats([])
    };
  }
  
  // Get indices to remove (all except first in each group)
  const indicesToRemove = [];
  duplicateGroups.forEach(group => {
    group.slice(1).forEach(({ index }) => {
      indicesToRemove.push(index);
    });
  });
  
  const filteredQuestions = removeDuplicatesByIndices(questions, indicesToRemove);
  const stats = getDuplicateStats(duplicateGroups);
  
  return {
    questions: filteredQuestions,
    removedCount: indicesToRemove.length,
    duplicateGroups: duplicateGroups,
    stats: stats
  };
};

/**
 * Find potential duplicates for a single question
 * @param {Object} targetQuestion - Question to check for duplicates
 * @param {Array} questions - Array of all questions
 * @param {number} threshold - Similarity threshold
 * @param {string} algorithm - Similarity algorithm
 * @returns {Array} - Array of potential duplicates with similarity scores
 */
export const findPotentialDuplicates = (targetQuestion, questions, threshold = 0.7, algorithm = 'combined') => {
  const getSimilarity = (text1, text2) => {
    switch (algorithm) {
      case 'jaccard':
        return calculateJaccardSimilarity(text1, text2);
      case 'levenshtein':
        return calculateLevenshteinSimilarity(text1, text2);
      case 'combined':
      default:
        return calculateCombinedSimilarity(text1, text2);
    }
  };
  
  const potentialDuplicates = [];
  
  questions.forEach((question, index) => {
    if (question === targetQuestion) return;
    
    const similarity = getSimilarity(
      targetQuestion.questionText,
      question.questionText
    );
    
    if (similarity >= threshold) {
      potentialDuplicates.push({
        question,
        index,
        similarity
      });
    }
  });
  
  return potentialDuplicates.sort((a, b) => b.similarity - a.similarity);
};
